import { Slice } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'

import { InitConfig } from './Types'
import { handlePaste } from './utils'

const PastePluginKey = new PluginKey('PastePlugin')

const stripStyles = (element: HTMLElement, commentExists: (id: string) => boolean) => {
  let whitelist: string[] = []
  let newStyleValue = ''
  switch (element.tagName) {
    case 'TABLE':
    case 'TR':
      whitelist = ['background']
      break
    case 'TD':
    case 'TH':
      whitelist = [
        'vertical-align',
        'width',
        'height',
        'background',
        'border-left',
        'border-right',
        'border-top',
        'border-bottom',
      ]
      break
    case 'SPAN':
      // 'font-style' is unsupported, italics are handled via the `em` tag
      // 'color' picks it up from data-color (for some reason) and since this only strips _STYLES_ it gets preserved
      whitelist = ['font-weight', 'font-size', 'font-family']
      break

    default:
      whitelist = []
      break
  }
  if (whitelist.length) {
    const propertyValues = new Map<(typeof whitelist)[number], string>()
    whitelist.forEach((styleKey) => {
      const propertyValue = element.style.getPropertyValue(styleKey)
      if (propertyValue) {
        propertyValues.set(styleKey, propertyValue)
      }
    })
    newStyleValue = Array.from(propertyValues.entries())
      .map(([k, v]) => `${k}: ${v};`)
      .join('')
  }
  element.setAttribute('style', newStyleValue)

  if (element instanceof HTMLElement && element.dataset?.type === 'comment') {
    removeExternalCommentMarks(element, commentExists)
  }

  Array.from(element.children as HTMLCollectionOf<HTMLElement>).forEach((e) => stripStyles(e, commentExists))
}

export const PastePlugin = (config: InitConfig) =>
  new Plugin({
    key: PastePluginKey,
    state: {
      init() {
        return {
          rootStore: config.rootStore,
          queryClient: config.queryClient,
        }
      },
      apply(_tr, state) {
        return state
      },
    },
    props: {
      transformPastedHTML: (html: string) => {
        const wrapper = document.createElement('div')
        wrapper.innerHTML = html

        const commentExists = (commentId: string): boolean => {
          // We should fall on the safe side and assume everything is a valid if we don't have access to the store
          if (config.rootStore == null) return true
          return config.rootStore.comments.getByUuid(commentId) != null
        }
        stripStyles(wrapper, commentExists)
        return wrapper.innerHTML
      },
      handlePaste: (view: EditorView, e: Event, slice: Slice) => {
        const { queryClient } = PastePluginKey.getState(view.state) as InitConfig
        return handlePaste(queryClient)(view, e, slice)
      },
    },
  })

const removeExternalCommentMarks = (element: HTMLElement, commentExists: (id: string) => boolean) => {
  const referencesValidComment = element.dataset.uuid && commentExists(element.dataset.uuid)

  if (!referencesValidComment) {
    const wrapper = document.createElement('span')
    wrapper.innerHTML = element.innerHTML

    element.replaceWith(wrapper)
  }
}
